import { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { colors } from '../styles/theme';
import { CoachScreen, type CoachClient } from './CoachScreen';
import type { ChatCopy } from './ChatScreen';
import { useRefreshingLoad } from '../hooks/useRefreshingLoad';
import { errorMessage } from '../utils/errorMessage';
import { formatRelativeTime } from '../utils/relativeTime';
import { MAX_PERSON_NAME } from '../utils/personNames';
import type { CoachTopic } from '../types/coach';
import type { PeopleResponse, Person } from '../types/people';

export interface PeopleClient {
  load(offset: number): Promise<PeopleResponse>;
  person(edgeId: string): Promise<Person>;
  /** The owner's own name for someone; an empty name goes back to the one their phone knows. */
  rename(edgeId: string, name: string): Promise<Person>;
  chat(topic: CoachTopic): CoachClient;
}

const avatarColors = ['#7c3aed', '#db2777', '#0891b2', '#ca8a04', '#059669', '#4f46e5'];
const initials = (name: string) =>
  /^[+\d\s()-]+$/.test(name)
    ? '#'
    : name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => [...word][0]?.toUpperCase())
        .join('');

function Avatar({ person, size }: { person: Person; size: number }) {
  const [failed, setFailed] = useState(false);
  const shape = { width: size, height: size, borderRadius: size / 2 };
  if (person.photoUrl && !failed)
    return (
      <Image
        source={{ uri: person.photoUrl }}
        style={shape}
        accessibilityIgnoresInvertColors
        onError={() => setFailed(true)}
      />
    );
  const hue = [...person.name].reduce((total, char) => total + char.charCodeAt(0), 0);
  return (
    <View
      style={[shape, styles.initials, { backgroundColor: avatarColors[hue % avatarColors.length] }]}
    >
      <Text style={[styles.initialsText, { fontSize: size * 0.36 }]}>{initials(person.name)}</Text>
    </View>
  );
}

const subline = (person: Person) =>
  person.lastChatAt
    ? `You talked about them ${formatRelativeTime(person.lastChatAt).toLowerCase()}`
    : `Last message ${formatRelativeTime(person.lastMessageAt).toLowerCase()}`;

const personCopy = (name: string): ChatCopy => ({
  title: name,
  subtitle: `You and ${name}.`,
  welcome: `You and ${name}.`,
  description: 'How you talk, what matters to them, and how to show up.',
  placeholder: `Ask about you and ${name}…`,
  suggestions: [
    ['How we talk', `How do ${name} and I usually talk?`],
    ['What matters to them', `What seems to matter most to ${name}?`],
    ['Reach out', `What’s a good way to reach out to ${name} this week?`],
  ],
});

function PersonChat({
  edgeId,
  known,
  client,
  onBack,
}: {
  edgeId: string;
  known?: Person;
  client: PeopleClient;
  onBack: () => void;
}) {
  const [found, setFound] = useState<Person | null>(null);
  const [error, setError] = useState('');
  const [renamed, setRenamed] = useState<Person | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (known) return;
    let current = true;
    client
      .person(edgeId)
      .then((person) => {
        if (current) setFound(person);
      })
      .catch((failure) => {
        if (current) setError(errorMessage(failure, 'Couldn’t find them.'));
      });
    return () => {
      current = false;
    };
  }, [client, edgeId, known]);
  const person = renamed ?? known ?? found;
  const saveName = async () => {
    if (draft === null || saving) return;
    if (draft.trim() === person?.name) {
      setDraft(null);
      return;
    }
    setSaving(true);
    setError('');
    try {
      setRenamed(await client.rename(edgeId, draft.trim()));
      setDraft(null);
    } catch (failure) {
      setError(errorMessage(failure, 'Couldn’t rename them. Please try again.'));
    } finally {
      setSaving(false);
    }
  };
  const chat = useMemo(() => client.chat({ kind: 'person', id: edgeId }), [client, edgeId]);
  return (
    <View style={styles.detail}>
      <View style={styles.detailHeader}>
        <TouchableOpacity onPress={onBack} accessibilityRole="button">
          <Text style={styles.back}>← Relationships</Text>
        </TouchableOpacity>
        {person && (
          <View style={styles.detailPerson}>
            <Avatar key={person.photoUrl ?? 'initials'} person={person} size={44} />
            {draft === null ? (
              <View style={styles.detailCopy}>
                <Text style={styles.name} accessibilityRole="header">
                  {person.name}
                </Text>
                <Text style={styles.caption}>
                  {person.messageCount.toLocaleString()} messages · {subline(person)}
                </Text>
              </View>
            ) : (
              <TextInput
                style={[styles.search, styles.detailCopy]}
                value={draft}
                onChangeText={setDraft}
                onSubmitEditing={() => void saveName()}
                maxLength={MAX_PERSON_NAME}
                autoFocus
                placeholder="Their name, as you know them"
                placeholderTextColor={colors.textSecondary}
                accessibilityLabel="Name"
              />
            )}
            {draft === null ? (
              <TouchableOpacity
                onPress={() => setDraft(person.name)}
                accessibilityRole="button"
                accessibilityLabel={`Rename ${person.name}`}
              >
                <Text style={styles.back}>Rename</Text>
              </TouchableOpacity>
            ) : (
              <>
                <TouchableOpacity
                  onPress={() => void saveName()}
                  disabled={saving}
                  accessibilityRole="button"
                >
                  <Text style={styles.back}>{saving ? 'Saving…' : 'Save'}</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setDraft(null)} accessibilityRole="button">
                  <Text style={styles.back}>Cancel</Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        )}
        {Boolean(error) && <Text style={styles.caption}>{error}</Text>}
      </View>
      {person && (
        <View style={styles.chat}>
          <CoachScreen key={edgeId} client={chat} copy={personCopy(person.name)} />
        </View>
      )}
    </View>
  );
}

export function PeopleScreen({
  client,
  selected,
  onSelect,
  onConnect,
}: {
  client: PeopleClient;
  selected: string | null;
  onSelect: (edgeId: string | null) => void;
  onConnect?: () => void;
}) {
  const [refresh, setRefresh] = useState(0);
  const { data, error } = useRefreshingLoad(() => client.load(0), [client, refresh]);
  const [more, setMore] = useState<PeopleResponse | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState('');
  useEffect(() => setMore(null), [data]);
  const people = [...(data?.people ?? []), ...(more?.people ?? [])];
  const nextOffset = more ? more.nextOffset : data?.nextOffset;

  if (selected)
    return (
      <PersonChat
        edgeId={selected}
        known={people.find((person) => person.edgeId === selected)}
        client={client}
        onBack={() => {
          onSelect(null);
          setRefresh((value) => value + 1);
        }}
      />
    );
  if (!data)
    return (
      <View style={styles.center}>
        {error ? (
          <>
            <Text style={styles.caption}>{error}</Text>
            <TouchableOpacity onPress={() => setRefresh((value) => value + 1)}>
              <Text style={styles.back}>Try again</Text>
            </TouchableOpacity>
          </>
        ) : (
          <ActivityIndicator color={colors.primary} />
        )}
      </View>
    );

  const search = query.trim().toLowerCase();
  const shown = search
    ? people.filter((person) => person.name.toLowerCase().includes(search))
    : people;
  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.heading}>
        <Text style={styles.eyebrow}>THE PEOPLE IN YOUR STORY</Text>
        <Text style={styles.title} accessibilityRole="header">
          Relationships
        </Text>
        <Text style={styles.caption}>Everyone you write to. Pick someone to talk about.</Text>
      </View>
      {people.length > 0 && (
        <TextInput
          style={styles.search}
          value={query}
          onChangeText={setQuery}
          placeholder="Find someone"
          placeholderTextColor={colors.textSecondary}
          accessibilityLabel="Find someone"
        />
      )}
      {people.length ? (
        <View style={styles.grid}>
          {shown.map((person) => (
            <TouchableOpacity
              key={person.edgeId}
              style={styles.card}
              activeOpacity={0.86}
              onPress={() => onSelect(person.edgeId)}
              accessibilityRole="button"
              accessibilityLabel={[person.name, person.relationship, subline(person)]
                .filter(Boolean)
                .join('. ')}
            >
              <View style={styles.cardTop}>
                <Avatar key={person.photoUrl ?? 'initials'} person={person} size={56} />
                {person.relationship && (
                  <Text style={styles.relationship} numberOfLines={1}>
                    {person.relationship}
                  </Text>
                )}
              </View>
              <Text style={styles.name} numberOfLines={1}>
                {person.name}
              </Text>
              <Text style={styles.caption} numberOfLines={2}>
                {subline(person)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      ) : (
        <View style={styles.empty}>
          <Text style={styles.name}>No one here yet.</Text>
          <Text style={[styles.caption, styles.centered]}>
            Once your conversations sync, everyone you write to shows up here.
          </Text>
          {onConnect && (
            <TouchableOpacity style={styles.action} onPress={onConnect} accessibilityRole="button">
              <Text style={styles.actionText}>Connect your apps</Text>
            </TouchableOpacity>
          )}
        </View>
      )}
      {nextOffset != null && (
        <TouchableOpacity
          style={styles.more}
          disabled={loadingMore}
          accessibilityRole="button"
          onPress={() => {
            setLoadingMore(true);
            client
              .load(nextOffset)
              .then((page) =>
                setMore((current) => ({
                  people: [...(current?.people ?? []), ...page.people],
                  nextOffset: page.nextOffset,
                }))
              )
              .catch(() => undefined)
              .finally(() => setLoadingMore(false));
          }}
        >
          <Text style={styles.back}>{loadingMore ? 'Loading…' : 'Show more people'}</Text>
        </TouchableOpacity>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { width: '100%', maxWidth: 1040, alignSelf: 'center', padding: 32, gap: 20 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 32 },
  heading: { gap: 10 },
  eyebrow: { color: colors.textSecondary, fontSize: 11, fontWeight: '600', letterSpacing: 2 },
  title: { color: colors.text, fontSize: 36, fontWeight: '500', letterSpacing: -1 },
  caption: { color: colors.textSecondary, fontSize: 13, lineHeight: 20 },
  centered: { textAlign: 'center', maxWidth: 420 },
  search: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.surfaceLight,
    color: colors.text,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 14,
    maxWidth: 360,
  },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 14 },
  card: {
    flexGrow: 1,
    flexBasis: 200,
    maxWidth: 320,
    backgroundColor: colors.surface,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: colors.surfaceLight,
    padding: 18,
    gap: 8,
  },
  cardTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  relationship: {
    flexShrink: 1,
    color: colors.textSecondary,
    backgroundColor: colors.surfaceLight,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
    fontSize: 12,
    overflow: 'hidden',
  },
  initials: { alignItems: 'center', justifyContent: 'center' },
  initialsText: { color: colors.text, fontWeight: '600' },
  name: { color: colors.text, fontSize: 16, fontWeight: '500' },
  empty: {
    alignItems: 'center',
    gap: 12,
    padding: 40,
    borderRadius: 20,
    backgroundColor: colors.surface,
  },
  action: {
    backgroundColor: colors.primary,
    paddingVertical: 12,
    paddingHorizontal: 22,
    borderRadius: 12,
  },
  actionText: { color: colors.text, fontSize: 14, fontWeight: '600' },
  more: { alignSelf: 'center', padding: 12 },
  back: { color: colors.textSecondary, fontSize: 13 },
  detail: { flex: 1, minHeight: 0 },
  detailHeader: {
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 12,
    gap: 14,
    borderBottomWidth: 1,
    borderBottomColor: colors.surfaceLight,
  },
  detailPerson: { flexDirection: 'row', alignItems: 'center', gap: 14 },
  detailCopy: { flex: 1, minWidth: 0 },
  chat: { flex: 1, minHeight: 0 },
});
